'use client'

import * as React from 'react'

import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { LessonOverviewButtonState, LessonType } from '../../components'
import { LessonOverviewButton } from '../../components'

export interface OverviewLesson {
  id: string
  index: number
  title: string
  lessonType: LessonType
  state: LessonOverviewButtonState
  stars?: 0 | 1 | 2 | 3
  guide?: unknown
  onClick: () => void
}

interface LessonOverviewPageProps {
  backgroundImageUrl: string | null
  title: string
  lessons: OverviewLesson[]
  /** Render a dark overlay even when backgroundImageUrl is null (e.g. when the background is provided by a parent layout) */
  overlay?: boolean
}

export function LessonOverviewPage({ backgroundImageUrl, title, lessons, overlay }: LessonOverviewPageProps) {
  return (
    <div className='srd:relative srd:min-h-full srd:max-h-[calc(100dvh-var(--navbar-height,0px))] srd:h-[calc(100dvh-var(--navbar-height,0px))] srd:overflow-y-auto'>
      {backgroundImageUrl && (
        <div
          className='srd:absolute srd:inset-0 srd:bg-cover srd:bg-center'
          style={{ backgroundImage: `url(${backgroundImageUrl})` }}
        />
      )}
      {(backgroundImageUrl || overlay) && <div className='srd:absolute srd:inset-0 srd:bg-neutral-black/50' />}
      <div className='srd:relative srd:flex srd:flex-col srd:items-center srd:gap-4 srd:py-6 srd:px-4'>
        <h1
          className={cn(
            typographyVariants({ variant: 'headline-md' }),
            'srd:text-neutral-white srd:text-center srd:max-w-[1200px] srd:mx-auto srd:w-full',
          )}
        >
          {title}
        </h1>
        <div className='srd:flex srd:flex-wrap srd:justify-center srd:gap-8 srd:w-full srd:max-w-[800px] srd:mx-auto'>
          {lessons.map((lesson) => (
            <React.Fragment key={lesson.id}>{renderLesson(lesson)}</React.Fragment>
          ))}
        </div>
      </div>
    </div>
  )
}

function renderLesson(lesson: OverviewLesson) {
  const { index, title, lessonType, onClick } = lesson

  if (lesson.state === 'completed') {
    return (
      <LessonOverviewButton
        index={index}
        title={title}
        lessonType={lessonType}
        onClick={onClick}
        state='completed'
        stars={lesson.stars ?? 0}
        className='srd:h-[172px]'
      />
    )
  }
  if (lesson.state === 'preview') {
    return (
      <LessonOverviewButton
        index={index}
        title={title}
        lessonType={lessonType}
        onClick={onClick}
        state='preview'
        guide={lesson.guide}
        className='srd:h-[172px]'
      />
    )
  }
  return (
    <LessonOverviewButton
      index={index}
      title={title}
      lessonType={lessonType}
      onClick={onClick}
      state={lesson.state}
      className='srd:h-[172px]'
    />
  )
}
